import { useState, useCallback, useRef } from 'react'
import { getProviders } from '@/api/providersAPI'
import { getManagers } from '@/api/managersAPI'
import { getUsers } from '@/api/usersAPI'

export interface CompanyOption {
  id: number
  name: string
}

export interface UserOption {
  id: number
  name: string
}

// Cache global para evitar requests duplicados
const searchCache = new Map<string, { data: any[], timestamp: number }>()
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

// Cache para datos iniciales (sin búsqueda)
const initialDataCache = new Map<string, { data: any[], timestamp: number }>()

const getCachedData = (cacheKey: string, fetchFunction: () => Promise<any>) => {
  const cached = searchCache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return Promise.resolve(cached.data)
  }
  
  return fetchFunction().then(response => {
    // The API now returns the items directly, not nested in response.data.items
    const items = Array.isArray(response) ? response : []
    
    // Mapear los datos al formato esperado
    const mappedData = items.map((item: any) => ({
      id: item.id,
      name: item.name
    }))
    
    searchCache.set(cacheKey, {
      data: mappedData,
      timestamp: Date.now()
    })
    return mappedData
  })
}

const getInitialData = (cacheKey: string, fetchFunction: () => Promise<any[]>) => {
  const cached = initialDataCache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return Promise.resolve(cached.data)
  }
  
  return fetchFunction().then(data => {
    initialDataCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    })
    return data
  })
}

// Hook para búsqueda asíncrona de proveedores
export const useAsyncProviders = () => {
  const searchProviders = async (query: string): Promise<CompanyOption[]> => {
    if (!query) {
      // Si no hay búsqueda, traer los primeros proveedores
      try {
        const results = await getCachedData('providers-initial', () => getProviders({
          page: 1,
          per_page: 50
        }))
        return results
      } catch (error) {
        return []
      }
    }
    try {
      const results = await getCachedData('providers', () => getProviders({
        page: 1,
        per_page: 50,
        search: query
      }))
      return results
    } catch (error) {
      return []
    }
  }

  // Función para limpiar el cache de proveedores
  const clearProvidersCache = () => {
    searchCache.clear()
  }

  return { searchProviders, clearProvidersCache }
}

// Hook para búsqueda asíncrona de operadores logísticos
export const useAsyncManagers = () => {
  const searchManagers = async (query: string): Promise<CompanyOption[]> => {
    if (!query) {
      // Si no hay búsqueda, traer los primeros operadores
      try {
        const results = await getCachedData('managers-initial', () => getManagers({
          page: 1,
          per_page: 50
        }))
        return results
      } catch (error) {
        return []
      }
    }
    try {
      const results = await getCachedData('managers', () => getManagers({
        page: 1,
        per_page: 50,
        search: query
      }))
      return results
    } catch (error) {
      return []
    }
  }

  // Función para limpiar el cache de operadores
  const clearManagersCache = () => {
    searchCache.clear()
  }

  return { searchManagers, clearManagersCache }
}

// Hook para búsqueda asíncrona de usuarios
export const useAsyncUsers = () => {
  const searchUsers = async (query: string): Promise<UserOption[]> => {
    try {
      const results = await getInitialData('users', () => getUsers({
        page: 1,
        per_page: 50,
        search: query
      }))
      
      // Mapear los datos al formato esperado
      return results.map((user: any) => ({
        id: user.id,
        name: user.displayName || user.username || `Usuario ${user.id}`
      }))
    } catch (error) {
      return []
    }
  }

  // Función para limpiar el cache de usuarios
  const clearUsersCache = () => {
    initialDataCache.clear()
  }

  return { searchUsers, clearUsersCache }
}

// Función para limpiar todos los caches
export const clearAllCaches = () => {
  searchCache.clear()
  initialDataCache.clear()
}

// Función para limpiar caches específicos
export const clearProviderAndManagerCaches = () => {
  searchCache.clear()
}
